import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  FileQuestion,
  ChevronDown,
  ChevronUp,
  Lock,
  Copy,
  Star,
  Loader2
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BeltIndicator } from '../shared/BeltBadge';
import { useBelts } from '../../contexts/BeltContext';
import { getAuthHeaders } from '../../services/adminApi';

import { PageTitle, HtmlContentEditor } from './shared';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Question type options
const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'short_answer', label: 'Short Answer' },
  { value: 'lineage', label: 'Lineage (Reference Resource)' },
];

// ============================================
// Question Option Editor (for multiple choice)
// ============================================
const OptionEditor = ({ options, onChange }) => {
  const addOption = () => {
    onChange([...options, { id: crypto.randomUUID(), text: '', is_correct: false }]);
  };

  const removeOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    onChange(newOptions);
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    // If setting one as correct, unset others
    if (field === 'is_correct' && value) {
      newOptions.forEach((opt, i) => {
        if (i !== index) opt.is_correct = false;
      });
    }
    onChange(newOptions);
  };

  return (
    <div className="space-y-2">
      <Label>Answer Options</Label>
      {options.map((option, index) => (
        <div key={option.id || index} className="flex items-center gap-2">
          {/* The label is the radio's touch target (44px on touch layouts). */}
          <label className="flex shrink-0 cursor-pointer items-center justify-center admin-touch:min-w-[44px]">
            <input
              type="radio"
              checked={option.is_correct}
              onChange={(e) => updateOption(index, 'is_correct', e.target.checked)}
              aria-label={`Option ${index + 1} is correct`}
              className="h-4 w-4 text-purple-600"
            />
          </label>
          <Input
            value={option.text}
            onChange={(e) => updateOption(index, 'text', e.target.value)}
            placeholder={`Option ${index + 1}`}
            className="flex-1"
          />
          {options.length > 2 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeOption(index)}
            >
              <X className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addOption}>
        <Plus className="h-4 w-4 mr-1" />
        Add Option
      </Button>
      <p className="text-xs text-gray-500 dark:text-gray-400">Select the radio button next to the correct answer</p>
    </div>
  );
};

// ============================================
// Question Editor Modal
// ============================================
const QuestionEditor = ({ question, resources, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    question_type: question?.question_type || 'multiple_choice',
    text: question?.text || '',
    options: question?.options || [
      { id: crypto.randomUUID(), text: '', is_correct: false },
      { id: crypto.randomUUID(), text: '', is_correct: false },
    ],
    correct_answer: question?.correct_answer || '',
    keywords: question?.keywords || [],
    resource_id: question?.resource_id || '',
    points: question?.points || 10,
    bonus_points: question?.bonus_points || 0,
    explanation: question?.explanation || '',
  });
  const [keywordsInput, setKeywordsInput] = useState(
    question?.keywords?.join(', ') || ''
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      keywords: keywordsInput.split(',').map(k => k.trim()).filter(Boolean),
    };
    onSave(data);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={question?.id ? 'Edit question' : 'Add question'}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <FileQuestion className="h-5 w-5 text-blue-600" />
            {question?.id ? 'Edit Question' : 'Add Question'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Question Type */}
            <div>
              <Label>Question Type</Label>
              <select
                value={formData.question_type}
                onChange={(e) => setFormData({ ...formData, question_type: e.target.value })}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background text-foreground"
              >
                {QUESTION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Question Text */}
            <div>
              <Label>Question Text</Label>
              <textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg min-h-[80px] bg-background text-foreground"
                placeholder="Enter your question..."
                required
              />
            </div>

            {/* Multiple Choice Options */}
            {formData.question_type === 'multiple_choice' && (
              <OptionEditor
                options={formData.options}
                onChange={(options) => setFormData({ ...formData, options })}
              />
            )}

            {/* Short Answer Fields */}
            {formData.question_type === 'short_answer' && (
              <>
                <div>
                  <Label>Expected Answer (for auto-grading reference)</Label>
                  <Input
                    value={formData.correct_answer}
                    onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                    placeholder="The expected answer"
                  />
                </div>
                <div>
                  <Label>Keywords (comma-separated, for partial matching)</Label>
                  <Input
                    value={keywordsInput}
                    onChange={(e) => setKeywordsInput(e.target.value)}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Short answers require admin review for grading
                  </p>
                </div>
              </>
            )}

            {/* Lineage Question Fields */}
            {formData.question_type === 'lineage' && (
              <>
                <div>
                  <Label>Linked Resource (optional)</Label>
                  <select
                    value={formData.resource_id}
                    onChange={(e) => setFormData({ ...formData, resource_id: e.target.value })}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background text-foreground"
                  >
                    <option value="">-- Select a resource --</option>
                    {resources.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.title}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Students can view this resource while answering
                  </p>
                </div>
                <div>
                  <Label>Keywords for partial credit</Label>
                  <Input
                    value={keywordsInput}
                    onChange={(e) => setKeywordsInput(e.target.value)}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>
              </>
            )}

            {/* Points */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Points</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Bonus Points (for speed/perfect)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.bonus_points}
                  onChange={(e) => setFormData({ ...formData, bonus_points: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Explanation */}
            <div>
              <Label>Answer Explanation (shown after quiz)</Label>
              <textarea
                value={formData.explanation}
                onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg min-h-[60px] bg-background text-foreground"
                placeholder="Explain why this is the correct answer..."
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="adminOutline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" variant="adminPrimary">
                <Save className="h-4 w-4 mr-2" />
                Save Question
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================
// Quiz Editor Panel
// ============================================
const QuizEditor = ({ quiz, programs, resources, onSave, onCancel, onImageUpload }) => {
  const { getBeltOptions } = useBelts();
  const beltOptions = getBeltOptions();

  const [formData, setFormData] = useState({
    title: quiz?.title || '',
    description: quiz?.description || '',
    sources_html: quiz?.sources_html || '',
    required_belt: quiz?.required_belt || '',
    required_program_ids: quiz?.required_program_ids || [],
    time_limit_minutes: quiz?.time_limit_minutes || 0,
    passing_score: quiz?.passing_score || 70,
    allow_retake: quiz?.allow_retake ?? true,
    show_answers_after: quiz?.show_answers_after ?? true,
    is_active: quiz?.is_active ?? true,
    is_featured: quiz?.is_featured ?? false,
  });
  const [questions, setQuestions] = useState(quiz?.questions || []);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleProgramToggle = (programId) => {
    const current = formData.required_program_ids;
    if (current.includes(programId)) {
      setFormData({
        ...formData,
        required_program_ids: current.filter(id => id !== programId)
      });
    } else {
      setFormData({
        ...formData,
        required_program_ids: [...current, programId]
      });
    }
  };

  const handleSaveQuestion = (questionData) => {
    if (editingQuestion?.id) {
      // Update existing
      setQuestions(prev => prev.map(q =>
        q.id === editingQuestion.id ? { ...q, ...questionData } : q
      ));
    } else {
      // Add new
      setQuestions(prev => [
        ...prev,
        { ...questionData, id: crypto.randomUUID(), sort_order: prev.length }
      ]);
    }
    setEditingQuestion(null);
  };

  const handleDeleteQuestion = (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    setQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const moveQuestion = (index, direction) => {
    const newQuestions = [...questions];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= questions.length) return;
    [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
    setQuestions(newQuestions.map((q, i) => ({ ...q, sort_order: i })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...formData, questions });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-background rounded-lg shadow-sm border p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Quiz Settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Quiz Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Belt Requirements - Yellow Belt"
              required
            />
          </div>

          <div className="md:col-span-2">
            <Label>Description</Label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg min-h-[80px] bg-background text-foreground"
              placeholder="Describe what this quiz covers..."
            />
          </div>

          <div className="md:col-span-2">
            <Label>Study Materials & Sources</Label>
            <p className="text-xs text-gray-500 mb-2">
              Optional learning resources that students can review before taking the quiz
            </p>
            <HtmlContentEditor
              value={formData.sources_html || ''}
              onChange={(html) => setFormData({ ...formData, sources_html: html })}
              placeholder="Add links to articles, videos, lineage pages, or other study materials..."
              onImageUpload={onImageUpload}
            />
          </div>

          <div>
            <Label>Time Limit (minutes, 0 = no limit)</Label>
            <Input
              type="number"
              min="0"
              value={formData.time_limit_minutes}
              onChange={(e) => setFormData({ ...formData, time_limit_minutes: parseInt(e.target.value) || 0 })}
            />
          </div>

          <div>
            <Label>Passing Score (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.passing_score}
              onChange={(e) => setFormData({ ...formData, passing_score: parseInt(e.target.value) || 70 })}
            />
          </div>

          <div>
            <Label>Minimum Belt Required</Label>
            <select
              value={formData.required_belt}
              onChange={(e) => setFormData({ ...formData, required_belt: e.target.value })}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background text-foreground"
            >
              <option value="">No restriction (all belts)</option>
              {beltOptions.map(belt => (
                <option key={belt.value} value={belt.value}>
                  {belt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label>Required Programs</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {programs.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">No programs found</p>
              ) : (
                programs.map(program => (
                  <label
                    key={program.id}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs cursor-pointer ${
                      formData.required_program_ids.includes(program.id)
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-700'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.required_program_ids.includes(program.id)}
                      onChange={() => handleProgramToggle(program.id)}
                      className="sr-only"
                    />
                    {program.name || program.title || `Program ${program.id?.slice(0, 6)}`}
                  </label>
                ))
              )}
            </div>
            {formData.required_program_ids.length === 0 && programs.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Available to all programs</p>
            )}
          </div>
        </div>

        {/* Toggle Options */}
        <div className="flex flex-wrap gap-4 border-t pt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.allow_retake}
              onChange={(e) => setFormData({ ...formData, allow_retake: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Allow Retakes</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.show_answers_after}
              onChange={(e) => setFormData({ ...formData, show_answers_after: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Show Answers After Completion</span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm flex items-center gap-1">
              <Star className="h-4 w-4 text-yellow-500" />
              Featured
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm">Active</span>
          </label>
        </div>

        {/* Questions */}
        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <FileQuestion className="h-5 w-5 text-blue-600" />
              Questions ({questions.length})
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditingQuestion({})}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Question
            </Button>
          </div>

          {questions.length === 0 ? (
            <div className="text-center text-gray-500 py-8 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed">
              <FileQuestion className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p>No questions yet. Add your first question above.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {questions
                .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                .map((question, index) => (
                  <div
                    key={question.id}
                    className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border"
                  >
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => moveQuestion(index, -1)}
                        disabled={index === 0}
                        aria-label={`Move question ${index + 1} up`}
                        className="flex items-center justify-center p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveQuestion(index, 1)}
                        disabled={index === questions.length - 1}
                        aria-label={`Move question ${index + 1} down`}
                        className="flex items-center justify-center p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Q{index + 1}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          question.question_type === 'multiple_choice'
                            ? 'bg-blue-100 text-blue-700'
                            : question.question_type === 'short_answer'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {QUESTION_TYPES.find(t => t.value === question.question_type)?.label}
                        </span>
                        <span className="text-xs text-gray-400">{question.points} pts</span>
                      </div>
                      <p className="text-sm line-clamp-2">{question.text}</p>
                    </div>

                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingQuestion(question)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteQuestion(question.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="adminOutline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="adminPrimary"
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {quiz?.id ? 'Update Quiz' : 'Create Quiz'}
          </Button>
        </div>
      </form>

      {/* Question Editor Modal */}
      {editingQuestion !== null && (
        <QuestionEditor
          question={editingQuestion}
          resources={resources}
          onSave={handleSaveQuestion}
          onCancel={() => setEditingQuestion(null)}
        />
      )}
    </div>
  );
};

// ============================================
// Main Quiz Builder Component
// ============================================
const QuizBuilder = () => {
  const { getBeltBySlug } = useBelts();
  const [quizzes, setQuizzes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchQuizzes = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/quizzes`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setQuizzes(data);
      }
    } catch (error) {
      console.error('Failed to fetch quizzes:', error);
    }
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/programs`);
      if (response.ok) {
        const data = await response.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    }
  }, []);

  const fetchResources = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/resources`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    }
  }, []);

  // Image upload handler for WYSIWYG editor
  const handleEditorImageUpload = async (file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('googleAuthToken')}`
        },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        return data.url; // Return the CDN URL
      } else {
        console.error('Failed to upload image to S3');
        return null;
      }
    } catch (error) {
      console.error('Upload error:', error);
      return null;
    }
  };

  useEffect(() => {
    Promise.all([fetchQuizzes(), fetchPrograms(), fetchResources()])
      .finally(() => setLoading(false));
  }, [fetchQuizzes, fetchPrograms, fetchResources]);

  const handleSave = async (quizData) => {
    try {
      const isNew = !editingQuiz?.id;
      const { questions, ...quizMetadata } = quizData;

      // Save quiz metadata
      const url = isNew
        ? `${API_BASE}/api/admin/quizzes`
        : `${API_BASE}/api/admin/quizzes/${editingQuiz.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(quizMetadata)
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Failed to save quiz');
        return;
      }

      const savedQuiz = await response.json();
      const quizId = savedQuiz.id;

      // If editing existing quiz, get existing questions to know what to delete
      let existingQuestionIds = [];
      if (!isNew) {
        const existingRes = await fetch(`${API_BASE}/api/admin/quizzes/${quizId}/questions`, {
          headers: getAuthHeaders()
        });
        if (existingRes.ok) {
          const existingQuestions = await existingRes.json();
          existingQuestionIds = existingQuestions.map(q => q.id);
        }
      }

      // Track which questions we're keeping
      const keptQuestionIds = [];

      // Save each question
      for (const question of questions || []) {
        const questionData = {
          ...question,
          quiz_id: quizId
        };

        if (question.id && existingQuestionIds.includes(question.id)) {
          // Update existing question
          await fetch(`${API_BASE}/api/admin/questions/${question.id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(questionData)
          });
          keptQuestionIds.push(question.id);
        } else {
          // Create new question
          const { id, ...newQuestionData } = questionData;
          await fetch(`${API_BASE}/api/admin/quizzes/${quizId}/questions`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(newQuestionData)
          });
        }
      }

      // Delete removed questions
      for (const existingId of existingQuestionIds) {
        if (!keptQuestionIds.includes(existingId)) {
          await fetch(`${API_BASE}/api/admin/questions/${existingId}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
          });
        }
      }

      await fetchQuizzes();
      setEditingQuiz(null);
    } catch (error) {
      console.error('Failed to save quiz:', error);
      alert('Failed to save quiz');
    }
  };

  const handleDelete = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/admin/quizzes/${quizId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchQuizzes();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete quiz');
      }
    } catch (error) {
      console.error('Failed to delete quiz:', error);
    }
  };

  const handleEditQuiz = async (quiz) => {
    // Fetch the quiz's questions from the backend
    try {
      const response = await fetch(`${API_BASE}/api/admin/quizzes/${quiz.id}/questions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const questions = await response.json();
        setEditingQuiz({ ...quiz, questions });
      } else {
        // If questions fetch fails, still allow editing but with empty questions
        setEditingQuiz({ ...quiz, questions: [] });
      }
    } catch (error) {
      console.error('Failed to fetch quiz questions:', error);
      setEditingQuiz({ ...quiz, questions: [] });
    }
  };

  const handleDuplicate = async (quiz) => {
    // Fetch questions first for duplication
    try {
      const response = await fetch(`${API_BASE}/api/admin/quizzes/${quiz.id}/questions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const questions = await response.json();
        const duplicatedQuiz = {
          ...quiz,
          id: undefined,
          title: `${quiz.title} (Copy)`,
          questions: questions.map(q => ({ ...q, id: undefined }))
        };
        setEditingQuiz(duplicatedQuiz);
      } else {
        setEditingQuiz({ ...quiz, id: undefined, title: `${quiz.title} (Copy)`, questions: [] });
      }
    } catch (error) {
      console.error('Failed to fetch quiz questions:', error);
      setEditingQuiz({ ...quiz, id: undefined, title: `${quiz.title} (Copy)`, questions: [] });
    }
  };

  const filteredQuizzes = quizzes
    .filter(quiz =>
      quiz.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quiz.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Sort by belt level (beginner to advanced)
      // Quizzes with no required_belt (null) should come first
      const beltA = a.required_belt ? getBeltBySlug(a.required_belt) : null;
      const beltB = b.required_belt ? getBeltBySlug(b.required_belt) : null;

      // No requirement comes first
      if (!beltA && beltB) return -1;
      if (beltA && !beltB) return 1;
      if (!beltA && !beltB) return 0;

      // Both have belts, compare by order
      return (beltA.order || 0) - (beltB.order || 0);
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (editingQuiz !== null) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <button
            onClick={() => setEditingQuiz(null)}
            className="hover:text-blue-600"
          >
            Quizzes
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-gray-100">
            {editingQuiz?.id ? 'Edit Quiz' : 'New Quiz'}
          </span>
        </div>
        <QuizEditor
          quiz={editingQuiz}
          programs={programs}
          resources={resources}
          onSave={handleSave}
          onCancel={() => setEditingQuiz(null)}
          onImageUpload={handleEditorImageUpload}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Quiz Builder"
        description="Create and manage quizzes for student learning"
        icon={<FileQuestion className="h-6 w-6" />}
        actions={
          <Button
            onClick={() => setEditingQuiz({})}
            variant="adminPrimary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Quiz
          </Button>
        }
      />

      {/* Search */}
      <div className="relative">
        <Input
          type="text"
          placeholder="Search quizzes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
        <FileQuestion className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      </div>

      {/* Quiz List */}
      {filteredQuizzes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed">
          <FileQuestion className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {searchQuery ? 'No quizzes found' : 'No quizzes yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchQuery
              ? 'Try a different search term'
              : 'Create your first quiz to get started'}
          </p>
          {!searchQuery && (
            <Button
              onClick={() => setEditingQuiz({})}
              variant="adminPrimary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Quiz
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredQuizzes.map(quiz => (
            <Card key={quiz.id} className={`hover:shadow-md transition-shadow ${!quiz.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{quiz.title}</h3>
                      {quiz.is_featured && (
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      )}
                      {!quiz.is_active && (
                        <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          Inactive
                        </span>
                      )}
                    </div>
                    {quiz.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                        {quiz.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <FileQuestion className="h-4 w-4" />
                        {quiz.question_count || 0} questions
                      </span>
                      {quiz.time_limit_minutes > 0 && (
                        <span>{quiz.time_limit_minutes} min</span>
                      )}
                      <span>{quiz.passing_score}% to pass</span>
                      {quiz.required_belt && (
                        <span className="flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          <BeltIndicator belt={quiz.required_belt} size="xs" />
                        </span>
                      )}
                    </div>
                    {quiz.attempt_stats && (
                      <div className="flex gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{quiz.attempt_stats.total_attempts} attempts</span>
                        <span>{quiz.attempt_stats.pass_rate?.toFixed(0)}% pass rate</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicate(quiz)}
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditQuiz(quiz)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(quiz.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuizBuilder;
